/**
 * Order Summary Component - Right Panel matching Figma design
 * Shows order summary with editing capability and place order button
 */

import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import {formatQuantityDisplay} from '../../../shared/order-editor/utils/quantity.utils'
import {MdRenderer} from '@hconnect/uikit'
import {Button as FundamentalButton} from '@hm/fundamental'
import {ArrowForward} from '@mui/icons-material'
import {Box, Typography, Button, CircularProgress, useTheme} from '@mui/material'
import {mdiPencilOutline} from '@mdi/js'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useShowTermsForOrderIntake} from '../../../../../OrderIntake/FeatureFlags'
import {useOrderIntakeData} from '../../../../../OrderIntake/hooks/useOrderIntakeData'
import {formattedDate} from '../../../../../Pages/Order/OrderDetails/components/utils'
import {useFeatureSlotManagementForRmcOrderIntakeEnabled} from '../../../shared/order-editor/components/fields/Time/useFeatureFlag'
import {RmcOrderEdit} from '../../../shared/order-editor/RmcOrderEdit'
import {useJobSite} from '../../contexts/JobSiteContext'
import {RmcOrder, RmcOrderUpdateOptions} from '../../types/order'
import {getMaterialVariationsDisplay} from '../../utils/orderUtils'
import {formatJobSiteInfo} from '../../utils/jobSiteUtils'
import {getRequestedTimeColor} from '../../utils/requestedTimeColor'
import {Banner} from '../shared/Banner'

/**
 * Helper function to get display time from order data
 */
const getDisplayTime = (
  order: RmcOrder,
  t: (key: string, fallback?: string) => string,
  language: string
): string => {
  // Fallback: extract time from formData.dateTime using formattedDate utility
  if (order.formData.dateTime && order.formData.dateTime[0]) {
    const [, timeOnly] = formattedDate(order.formData.dateTime[0], language)
    if (timeOnly) {
      return timeOnly
    }
  }

  // Default fallback
  return t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')
}

/**
 * Props for the Order Summary component
 */
interface RmcOrderSummaryProps {
  selectedOrder: RmcOrder | null
  orders: RmcOrder[]
  isPlacingOrders: boolean
  placementError: string | null
  canPlaceOrders: boolean
  hasDateResets?: boolean
  onPlaceOrders: () => Promise<void>
  onClearError: () => void
  onSelectOrder: (orderId: string | null) => void
  onOrderUpdate?: (formData: FieldsForm, options?: RmcOrderUpdateOptions) => void
}

/**
 * Order Summary component matching Figma design
 */
export const RmcOrderSummary: React.FC<RmcOrderSummaryProps> = ({
  selectedOrder,
  orders,
  isPlacingOrders,
  canPlaceOrders,
  hasDateResets = false,
  onPlaceOrders,
  onSelectOrder,
  onOrderUpdate
}) => {
  const [isEditMode, setIsEditMode] = React.useState(false)
  const [showDateResetBanner, setShowDateResetBanner] = React.useState(hasDateResets)
  const {jobSite} = useJobSite()
  const {t, i18n} = useTranslation()
  const theme = useTheme()
  const slotManagementEnabled = useFeatureSlotManagementForRmcOrderIntakeEnabled()

  // Get user profile data for country-specific features
  const {userProfileCountry} = useOrderIntakeData()

  // Check if terms should be shown based on feature flag
  const termsConfig = useShowTermsForOrderIntake(userProfileCountry)

  // Switch to edit mode when an order is selected
  React.useEffect(() => {
    if (selectedOrder) {
      setIsEditMode(true)
    } else {
      setIsEditMode(false)
    }
  }, [selectedOrder])

  // Update banner visibility when hasDateResets changes
  React.useEffect(() => {
    setShowDateResetBanner(hasDateResets)
  }, [hasDateResets])

  if (isEditMode && selectedOrder) {
    return (
      <RmcOrderEdit
        selectedOrder={selectedOrder}
        onClose={() => {
          setIsEditMode(false)
          onSelectOrder(null) // Deselect the order to return to summary view
        }}
        onOrderUpdate={onOrderUpdate}
        jobSite={jobSite}
      />
    )
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}
    >
      {/* Title */}
      <Box>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: '24px',
            color: '#29333d',
            fontFamily: 'Inter',
            marginBottom: '4px'
          }}
        >
          {t('orderIntake.rmc.orderManagement.orderSummary.title', 'Order Summary')}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            color: '#52667a',
            fontFamily: 'Inter'
          }}
        >
          {formatJobSiteInfo(jobSite)}
        </Typography>
      </Box>

      {/* Date Reset Information Banner */}
      {showDateResetBanner && (
        <Banner
          variant="info"
          message={t(
            'orderIntake.rmc.orderManagement.orderSummary.dateResetMessage',
            'The selected date and time have been set to the earliest available slot. Please review and confirm that they are as desired.'
          )}
          onClose={() => setShowDateResetBanner(false)}
          testId="date-reset-banner"
        />
      )}

      {/* Order Summary Content */}
      {orders.length > 0 ? (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
          {/* Grouped Orders by Material */}
          {(() => {
            // Group orders by material description AND material variations
            const groupedOrders = orders.reduce(
              (groups, order) => {
                const materialDescription = order.formData.material?.materialDescription || ''
                const materialVariations = getMaterialVariationsDisplay(order) || ''
                // Create composite key: material + material variations to ensure proper grouping
                const materialKey = `${materialDescription}||${materialVariations}`
                if (!groups[materialKey]) {
                  groups[materialKey] = []
                }
                groups[materialKey].push(order)
                return groups
              },
              {} as Record<string, RmcOrder[]>
            )

            return Object.entries(groupedOrders).map(([materialKey, materialOrders]) => {
              // Extract material description from composite key
              const materialDescription = materialKey.split('||')[0]

              // Sum quantities separately by type
              const totalFullLoads = materialOrders.reduce(
                (sum, o) =>
                  o.formData.quantity.type === 'fullLoads'
                    ? sum + o.formData.quantity.fullLoads
                    : sum,
                0
              )
              const totalQuantity = materialOrders.reduce(
                (sum, o) =>
                  o.formData.quantity.type === 'volume' ? sum + o.formData.quantity.volume : sum,
                0
              )

              const parts: string[] = []
              if (totalFullLoads > 0) {
                parts.push(
                  totalFullLoads === 1
                    ? t('orderIntake.rmc.steps.quantity.fullLoad', '{{count}} full load', {
                        count: totalFullLoads
                      })
                    : t('orderIntake.rmc.steps.quantity.fullLoads', '{{count}} full loads', {
                        count: totalFullLoads
                      })
                )
              }
              if (totalQuantity > 0) {
                parts.push(`${totalQuantity} M³`)
              }
              const quantityHeaderDisplay = parts.join(' + ')

              // Check if any order in this material group has material variations
              const hasMaterialVariations = materialOrders.some(
                (order) => getMaterialVariationsDisplay(order) !== null
              )

              // Get material variations from first order (they should be same for grouped materials)
              const materialVariations =
                materialOrders.length > 0 ? getMaterialVariationsDisplay(materialOrders[0]) : null

              return (
                <Box
                  key={materialDescription}
                  sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}
                >
                  {/* Material Group Header */}
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          lineHeight: '24px',
                          color: '#29333d',
                          fontFamily: 'Inter',
                          fontStyle: hasMaterialVariations ? 'italic' : 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {quantityHeaderDisplay} – {materialDescription}
                        {hasMaterialVariations ? ' *' : ''}
                      </Typography>
                      {materialVariations && (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            lineHeight: '16px',
                            color: '#29333d',
                            fontFamily: 'Inter',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          * {materialVariations}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Individual Orders in this Material Group */}
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    {materialOrders.map((order, index) => (
                      <Box
                        key={order.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom:
                            index < materialOrders.length - 1 ? '1px solid #f0f2f5' : 'none'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            flex: 1,
                            minWidth: 0
                          }}
                        >
                          {/* Status Badge */}
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 500,
                              lineHeight: '16px',
                              color: order.formData?.isConfirmedOrder ? '#00AB50' : '#ff8c00',
                              fontFamily: 'Inter',
                              marginBottom: '2px'
                            }}
                          >
                            {order.formData?.isConfirmedOrder
                              ? t(
                                  'orderIntake.rmc.orderManagement.orderSummary.confirmed',
                                  'Confirmed'
                                )
                              : t(
                                  'orderIntake.rmc.orderManagement.orderSummary.unconfirmed',
                                  'Unconfirmed'
                                )}
                          </Typography>

                          {/* Order Details - Quantity, Date and Time */}
                          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                lineHeight: '24px',
                                color: '#29333d',
                                fontFamily: 'Inter'
                              }}
                            >
                              {order.formData.quantity.type === 'fullLoads'
                                ? order.formData.quantity.fullLoads === 1
                                  ? t(
                                      'orderIntake.rmc.steps.quantity.fullLoad',
                                      '{{count}} full load',
                                      {count: order.formData.quantity.fullLoads}
                                    )
                                  : t(
                                      'orderIntake.rmc.steps.quantity.fullLoads',
                                      '{{count}} full loads',
                                      {count: order.formData.quantity.fullLoads}
                                    )
                                : formatQuantityDisplay(order.formData.quantity, t)}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 400,
                                lineHeight: '24px',
                                color: '#29333d',
                                fontFamily: 'Inter',
                                marginLeft: 'auto'
                              }}
                            >
                              {formattedDate(order.formData.dateTime[0], i18n.language)[0]} |{' '}
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 400,
                                  lineHeight: '24px',
                                  color: getRequestedTimeColor(
                                    theme.palette,
                                    slotManagementEnabled,
                                    order.requiresDispatchApproval
                                  ),
                                  fontFamily: 'Inter'
                                }}
                              >
                                {getDisplayTime(order, t, i18n.language)}
                              </Typography>
                            </Typography>

                            {/* Edit Icon */}
                            <FundamentalButton
                              icon={mdiPencilOutline}
                              variant="secondary"
                              size="small"
                              square
                              onClick={() => onSelectOrder(order.id)}
                              data-test-id={`edit-order-${order.id}`}
                            />
                          </Box>

                          {/* Plus Load Indicator */}
                          {order.formData.plusLoadExpected && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 400,
                                lineHeight: '16px',
                                color: '#52667a',
                                fontFamily: 'Inter'
                              }}
                            >
                              {t('orderIntake.rmc.orderManagement.plusLoad', '+ Plus load')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )
            })
          })()}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              color: '#52667a',
              fontFamily: 'Inter'
            }}
          >
            {t('orderIntake.rmc.orderManagement.orderSummary.noOrdersYet', 'No orders created yet')}
          </Typography>
        </Box>
      )}

      {/* Terms of Sales Note - Only for users where feature flag is enabled */}
      {termsConfig.enabled && (
        <Box
          sx={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            border: '1px solid #e8ecf0',
            '& p': {
              fontSize: '14px',
              lineHeight: '20px',
              color: '#52667a',
              fontFamily: 'Inter',
              fontWeight: 400,
              margin: 0
            },
            '& a': {
              color: '#016ad4',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }
          }}
        >
          <MdRenderer
            body={t('orderIntake.confirmationScreen.confirmationLink')}
            paragraphVariant="body2"
          />
        </Box>
      )}

      {/* Place Order Button */}
      <Button
        variant="contained"
        endIcon={
          isPlacingOrders ? (
            <CircularProgress
              size={16}
              sx={{
                color: '#52667a'
              }}
            />
          ) : (
            <ArrowForward />
          )
        }
        fullWidth
        disabled={!canPlaceOrders || isPlacingOrders}
        onClick={onPlaceOrders}
        sx={{
          height: '48px',
          textTransform: 'none',
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '22px',
          color: '#ffffff',
          backgroundColor: '#016ad4',
          fontFamily: 'Inter',
          boxShadow: '0px 2px 2px 0px rgba(0,0,0,0.08)',
          '&:hover': {
            backgroundColor: '#014ba8'
          },
          '&:disabled': {
            backgroundColor: '#e8ecf0',
            color: '#52667a'
          }
        }}
        data-test-id="place-order-button"
      >
        {isPlacingOrders
          ? t('orderIntake.rmc.orderManagement.orderSummary.placingOrders', 'Placing Orders...')
          : t('orderIntake.rmc.orderManagement.orderSummary.placeOrder', 'Place Order')}
      </Button>
    </Box>
  )
}
